import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, ChevronLeft, ChevronRight, Info, AlertCircle } from 'lucide-react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog } from '../ui/dialog';
import { cn } from "../../lib/utils";

// Create a custom DialogContent that doesn't include the Close button
const DialogContentWithoutCloseButton = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      {/* No close button here! */}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContentWithoutCloseButton.displayName = "DialogContentWithoutCloseButton";

interface PropertyImageCarouselModalProps {
  imageUrls: string[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
  imageInfos?: Array<{
    room?: string;
    description?: string;
    redFlags?: string[];
  }>;
  roomName?: string;
}

const PropertyImageCarouselModal: React.FC<PropertyImageCarouselModalProps> = ({ 
  imageUrls, 
  isOpen, 
  onClose,
  initialIndex = 0,
  imageInfos = [],
  roomName = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showInfo, setShowInfo] = useState(false);
  
  // Define a function to go to next/previous image
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
  }, [imageUrls.length]);
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  }, [imageUrls.length]);
  
  // Handle keyboard events
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (imageUrls.length === 0) return;
    
    if (e.key === 'ArrowRight') {
      goToNext();
    } else if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'i') {
      setShowInfo(prev => !prev);
    }
  }, [imageUrls.length, onClose, goToNext, goToPrevious]);

  // Set initial index when modal opens
  useEffect(() => {
    if (isOpen && imageUrls.length > 0 && initialIndex < imageUrls.length) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex, imageUrls.length]);
  
  // Add keyboard event listeners
  useEffect(() => {
    // Add event listener for keyboard navigation
    window.addEventListener('keydown', handleKeyDown);
    
    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  // No images to display
  if (imageUrls.length === 0) return null;
  
  // These are just aliases to the shared functions
  const handleNext = goToNext;
  const handlePrevious = goToPrevious;
  
  // Get current image info if available
  const currentInfo = imageInfos[currentIndex] || {};
  
  const openInNewWindow = () => {
    window.open(imageUrls[currentIndex], '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {
      // Reset info panel state when closing the modal
      if (isOpen) {
        setShowInfo(false);
      }
      onClose();
    }}>
      <DialogContentWithoutCloseButton className="max-w-7xl w-full h-[90vh] p-0 bg-black text-white border-gray-800">
        {/* Custom header with a single Close button */}
        <div className="flex justify-between items-center p-4 bg-black/90 border-b border-gray-800">
          <h2 className="text-lg font-semibold">
            {roomName || 'Property Images'} 
            {imageUrls.length > 1 ? ` (${currentIndex + 1}/${imageUrls.length})` : ''}
          </h2>
          
          <div className="flex items-center gap-2">
            {/* Toggle info panel button */}
            {(currentInfo.description || (currentInfo.redFlags && currentInfo.redFlags.length > 0)) && (
              <button 
                onClick={() => setShowInfo(!showInfo)}
                className={`p-1.5 rounded-full ${showInfo ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'} text-white transition-colors`}
                title={showInfo ? "Hide info" : "Show info"}
              >
                <Info className="h-4 w-4" />
              </button>
            )}
            
            {/* Open in new window button */}
            <button 
              onClick={openInNewWindow}
              className="p-1.5 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors"
              title="Open in new window"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            
            {/* Close button */}
            <button 
              onClick={onClose}
              className="p-1.5 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex h-[calc(100%-8rem)]">
          {/* Image Container - Takes full width */}
          <div className="relative flex items-center justify-center w-full h-full bg-black">
            <img
              src={imageUrls[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              className="max-w-[90%] max-h-[90%] object-contain"
            />
            
            {/* Always show information overlay at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-4 text-white">
              {currentInfo.description && (
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-white/90 mb-1">
                    Description
                  </h3>
                  <p className="text-sm text-white/80">
                    {currentInfo.description.length > 150 
                      ? `${currentInfo.description.substring(0, 150)}...` 
                      : currentInfo.description}
                  </p>
                </div>
              )}
              
              {currentInfo.redFlags && currentInfo.redFlags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-red-300 mb-1 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span>Issues Detected</span>
                    <span className="ml-1 text-xs px-1.5 py-0.5 bg-red-900/80 rounded-full">
                      {currentInfo.redFlags.length}
                    </span>
                  </h3>
                  
                  {/* Always show first issue, with count of others */}
                  <div>
                    <p className="text-sm text-white/80">
                      • {currentInfo.redFlags[0]}
                    </p>
                    {currentInfo.redFlags.length > 1 && (
                      <button 
                        onClick={() => setShowInfo(!showInfo)}
                        className="text-xs text-blue-400 hover:text-blue-300 mt-1 flex items-center gap-1"
                      >
                        Show {currentInfo.redFlags.length - 1} more {showInfo ? "↓" : "→"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Navigation Controls - Improved visibility and interaction */}
            {imageUrls.length > 1 && (
              <>
                <button 
                  onClick={handlePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button 
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
          
          {/* Full details panel - slides in from the side when requested */}
          {showInfo && currentInfo.redFlags && currentInfo.redFlags.length > 1 && (
            <div className="absolute right-0 top-16 bottom-24 w-1/3 p-6 overflow-auto bg-gray-900/90 border-l border-gray-800 animate-in slide-in-from-right duration-300 rounded-l-lg">
              <div className="mb-2 flex justify-between items-center">
                <h3 className="text-sm font-semibold text-red-300 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>All Issues</span>
                </h3>
                <button 
                  onClick={() => setShowInfo(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <ul className="pl-5 space-y-2">
                {currentInfo.redFlags.map((flag, idx) => (
                  <li key={idx} className="text-sm text-white list-disc">
                    {flag}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Thumbnail Navigation - Improved design */}
        {imageUrls.length > 1 && (
          <div className="flex overflow-x-auto p-4 bg-gray-900 border-t border-gray-800 gap-2 h-24 items-center justify-center overflow-y-hidden">
            {imageUrls.map((url, index) => (
              <div 
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`
                  relative w-16 h-16 flex-shrink-0 cursor-pointer overflow-hidden rounded-md
                  ${index === currentIndex 
                    ? 'ring-2 ring-blue-500 scale-110 z-10' 
                    : 'ring-1 ring-gray-600 opacity-70 hover:opacity-100 hover:ring-gray-400'}
                  transition-all duration-200
                `}
              >
                <img 
                  src={url} 
                  alt={`Thumbnail ${index + 1}`} 
                  className="w-full h-full object-cover"
                  loading="lazy" 
                />
                
                {/* Remove individual image issue indicators */}
              </div>
            ))}
          </div>
        )}
        
        {/* Keyboard shortcuts hint - only visible on larger screens */}
        <div className="absolute bottom-1 left-4 text-xs text-gray-500 hidden md:block">
          Keyboard: ← → arrows to navigate, i to toggle info, Esc to close
        </div>
      </DialogContentWithoutCloseButton>
    </Dialog>
  );
};

export default PropertyImageCarouselModal;